import User from "@/data/models/User";
import {
  DEFAULT_ADMIN_ID,
  IRES_ADMIN_ACCOUNT_ID,
} from "@/store/modules/UserModule";
import { getUserDisplayName } from "@/utils/UserUtils";

/**
 * Whether "Sicht auf" may show `user` to `activeUser`: a user of the client who can log in normally
 * (not deleted, not blocked; a licence is not required) other than the session user themselves and
 * the two technical admin accounts (Taimoor, 2026-10-13, ticket 2610050).
 *
 * @param user A user of the client.
 * @param activeUser Authenticated user of the current session.
 */
export function isSelectableTargetUser(
  user: User | null | undefined,
  activeUser: User | null | undefined,
): user is User {
  return (
    !!user &&
    !user.deleted &&
    !user.blocked &&
    user.id !== activeUser?.id &&
    user.id !== DEFAULT_ADMIN_ID &&
    user.id !== IRES_ADMIN_ACCOUNT_ID
  );
}

/**
 * Users the "view as user" (Sicht auf) picker may offer ({@link isSelectableTargetUser}), sorted by
 * display name.
 *
 * Shared by the cockpit toolbar and the time tracking history so both build the
 * same list and can hide the picker when it would have nothing to offer.
 *
 * @param users All users of the client (`UserModule.users`).
 * @param activeUser Authenticated user of the current session.
 * @returns New sorted array of the users that can be picked.
 */
export function getSelectableTargetUsers(
  users: User[],
  activeUser: User | null | undefined,
): User[] {
  return users
    .filter((user) => isSelectableTargetUser(user, activeUser))
    .sort((u1, u2) =>
      getUserDisplayName(u1).localeCompare(getUserDisplayName(u2)),
    );
}

/** Why a stored "Sicht auf" choice has to end by itself. */
export enum ViewAsEndReason {
  /** The chosen user was deleted or blocked, or is no longer in the client. */
  USER_UNAVAILABLE = "USER_UNAVAILABLE",
  /** The viewer lost the right to use "Sicht auf". */
  NO_RIGHT = "NO_RIGHT",
}

export interface ViewAsResolution {
  /** The user to show the app as, or `null` for the viewer's own view. */
  user: User | null;
  /** Set when the stored choice is no longer valid and has to end. */
  endReason: ViewAsEndReason | null;
}

/**
 * Resolves a stored "Sicht auf" choice against the current users and rights.
 *
 * The choice is kept as an id and read against the live user list, so a renamed user shows the new
 * name and a changed role applies at once. Nothing is decided while the inputs are not there yet
 * (the user list right after a refresh, role settings still on their defaults): the choice then
 * shows nothing, and ends nothing.
 *
 * @param targetUserId The stored choice, or `null`.
 * @param activeUser Authenticated user of the current session.
 * @param users All users of the client (`UserModule.users`).
 * @param mayUseViewAs Whether `activeUser` may use "Sicht auf" (`switchTargetUserAccess`).
 * @param areRightsKnown Whether the role settings behind `mayUseViewAs` have loaded.
 */
export function resolveViewAs(
  targetUserId: string | null,
  activeUser: User | null,
  users: User[],
  mayUseViewAs: boolean,
  areRightsKnown: boolean,
): ViewAsResolution {
  const none: ViewAsResolution = { user: null, endReason: null };

  if (!targetUserId || !activeUser) {
    return none;
  }

  // The list always holds the session user once it has loaded.
  const hasUsersLoaded = users.some((user) => user.id === activeUser.id);

  if (!hasUsersLoaded) {
    return none;
  }

  if (!mayUseViewAs) {
    return { user: null, endReason: areRightsKnown ? ViewAsEndReason.NO_RIGHT : null };
  }

  const target = users.find((user) => user.id === targetUserId);

  if (!isSelectableTargetUser(target, activeUser)) {
    return { user: null, endReason: ViewAsEndReason.USER_UNAVAILABLE };
  }

  return { user: target, endReason: null };
}
